import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Sparkles, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const FEATURE_LABEL = {
  ai_diagnose:      "AI Diagnose",
  video_uploads:    "Video uploads",
  cokeeper_invites: "Co-keeper invites",
};

const FEATURE_SUFFIX = { ai_diagnose: "/ month", video_uploads: "/ month", cokeeper_invites: "" };

const PLAN_FEATURES = {
  free: [
    "5 AI diagnoses / month",
    "10 video uploads / month",
    "1 co-keeper invite",
    "1 yard / apiary",
    "Inspection journals",
  ],
  pro:  [
    "Unlimited AI diagnoses",
    "Unlimited video uploads",
    "10 co-keeper invites",
    "Unlimited yards & multi-apiary support",
    "Smart Calendar — predictive seasonal nudges",
    "Voice-to-Journal — dictate while you inspect",
    "Hive Health Trends dashboard",
    "Compare hives side-by-side",
    "Treatments & feeding tracker (auto follow-ups)",
    "Weather-aware Inspection Forecast",
    "CSV exports + printable annual report",
  ],
  team: [
    "Everything in Pro",
    "Unlimited co-keeper invites",
    "Apiary-wide collaboration",
    "Priority support",
  ],
};

const PRICE_FMT = (n) => `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`;

export default function BillingTab() {
  const [sub, setSub] = useState(null);
  const [interval, setInterval] = useState("month");
  const [busy, setBusy] = useState(false);
  const [pollStatus, setPollStatus] = useState(null);
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();

  const load = async () => {
    try {
      const r = await api.get("/billing/subscription");
      setSub(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load billing");
    }
  };

  useEffect(() => { load(); }, []);

  // Poll Checkout status if we just returned from Stripe
  useEffect(() => {
    const sessionId = sp.get("session_id");
    const billingFlag = sp.get("billing");
    if (!sessionId || !billingFlag) return;

    let attempts = 0;
    let cancelled = false;
    setPollStatus({ state: "polling", message: "Confirming your payment…" });

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      if (attempts > 10) {
        setPollStatus({ state: "timeout", message: "Still pending — refresh in a minute or check your email." });
        return;
      }
      try {
        const r = await api.get(`/billing/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setPollStatus({ state: "paid", message: `Welcome to ${r.data.plan?.toUpperCase()}!` });
          toast.success(`Payment received — you're on ${r.data.plan?.toUpperCase()}.`);
          await load();
          // Notify other parts of the app (Layout sidebar pill) to refetch.
          window.dispatchEvent(new CustomEvent("billing:updated"));
          setTimeout(() => { setSp({}); setPollStatus(null); }, 2500);
          return;
        }
        if (r.data.payment_status === "expired" || r.data.payment_status === "failed") {
          setPollStatus({ state: "failed", message: "Payment didn't go through." });
          return;
        }
        setTimeout(tick, 2000);
      } catch {
        setPollStatus({ state: "failed", message: "Couldn't verify the payment." });
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sp]);

  const upgrade = async (plan) => {
    setBusy(true);
    try {
      const r = await api.post("/billing/checkout", {
        plan, interval, origin_url: window.location.origin,
      });
      window.location.href = r.data.url;  // hard redirect to Stripe
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  if (!sub) return <div className="text-sm text-stone-500 py-4" data-testid="billing-loading">Loading billing…</div>;

  const isPaid = sub.plan !== "free";
  const trialDaysLeft = sub.trial_end ? Math.max(0, Math.ceil((new Date(sub.trial_end) - new Date()) / 86400000)) : 0;
  const yearlyPrices = sub.prices;

  return (
    <div className="space-y-6" data-testid="billing-tab">
      {pollStatus && (
        <div className={`rounded-xl p-4 flex items-center gap-3 ${pollStatus.state === "paid" ? "bg-emerald-50 text-emerald-900 border border-emerald-200" : pollStatus.state === "failed" || pollStatus.state === "timeout" ? "bg-red-50 text-red-900 border border-red-200" : "bg-amber-50 text-amber-900 border border-amber-200"}`} data-testid="billing-poll-banner">
          {pollStatus.state === "paid" ? <CheckCircle2 className="w-5 h-5" /> : pollStatus.state === "polling" ? <Loader2 className="w-5 h-5 animate-spin" /> : <AlertCircle className="w-5 h-5" />}
          <span className="text-sm font-medium">{pollStatus.message}</span>
        </div>
      )}

      {/* Current plan card */}
      <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="current-plan-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Current plan</div>
            <div className="flex items-center gap-2 mt-1">
              <div className="font-display text-3xl font-bold text-stone-900" data-testid="current-plan-name">{sub.plan_label}</div>
              {sub.is_trialing && (
                <span className="text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200">
                  Trial · {trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} left
                </span>
              )}
              {sub.is_comped && (
                <span className="text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-0.5 rounded-full bg-violet-100 text-violet-900 border border-violet-200" data-testid="gift-access-badge">
                  {sub.gift_lifetime ? "Gift · Lifetime" : "Gift access"}
                </span>
              )}
              {!sub.is_trialing && !sub.is_comped && isPaid && sub.status === "active" && (
                <span className="text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-900 border border-emerald-200">Active</span>
              )}
            </div>
            {sub.current_period_end && !sub.gift_lifetime && (
              <div className="text-xs text-stone-500 mt-1">
                {sub.is_trialing ? "Trial ends " : sub.is_comped ? "Gift access until " : "Renews "} {new Date(sub.current_period_end).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </div>
            )}
            {sub.gift_lifetime && (
              <div className="text-xs text-stone-500 mt-1">Lifetime access · no expiry</div>
            )}
          </div>
        </div>

        {/* Usage bars — only relevant for Free tier (or counts on Pro/Team) */}
        <div className="grid sm:grid-cols-3 gap-4 mt-5">
          {Object.keys(FEATURE_LABEL).map((f) => (
            <UsageBar key={f} feature={f} usage={sub.usage[f]} limit={sub.limits[f]} />
          ))}
        </div>
      </div>

      {/* Plan picker — hidden for users with comp/gift access */}
      {(!isPaid || sub.is_trialing) && !sub.is_comped ? (
        <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="plan-picker">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
            <h3 className="font-display text-xl font-medium">Choose a plan</h3>
            <div className="inline-flex p-1 rounded-full bg-stone-100" data-testid="interval-toggle">
              <button onClick={() => setInterval("month")} className={`text-xs px-3 py-1 rounded-full font-medium ${interval === "month" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500"}`} data-testid="interval-month">Monthly</button>
              <button onClick={() => setInterval("year")}  className={`text-xs px-3 py-1 rounded-full font-medium ${interval === "year" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500"}`} data-testid="interval-year">Yearly · save 20%</button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {["pro", "team"].map((p) => {
              const price = yearlyPrices[p][interval];
              const monthly = interval === "year" ? price / 12 : price;
              return (
                <div key={p} className={`relative border rounded-2xl p-5 ${p === "pro" ? "border-amber-300 bg-amber-50/40 shadow-[0_8px_28px_rgba(245,158,11,0.18)]" : "border-stone-200"}`} data-testid={`plan-${p}`}>
                  {p === "pro" && <span className="absolute -top-2.5 left-5 text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-amber-500 text-stone-950">Most popular</span>}
                  <div className="font-display text-2xl font-bold capitalize">{p}</div>
                  <div className="text-stone-700 mt-1">
                    <span className="text-4xl font-black text-stone-950">{PRICE_FMT(monthly)}</span>
                    <span className="text-stone-500 text-sm"> / mo</span>
                    {interval === "year" && (
                      <div className="text-xs text-stone-500 mt-1">billed {PRICE_FMT(price)} / year</div>
                    )}
                  </div>
                  <ul className="space-y-1.5 mt-4 text-sm text-stone-700">
                    {PLAN_FEATURES[p].map((line, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" /> <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => upgrade(p)}
                    disabled={busy}
                    className={`w-full rounded-full mt-5 ${p === "pro" ? "bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold shadow-[0_0_24px_rgba(245,158,11,0.4)]" : "bg-stone-900 hover:bg-stone-800 text-white"}`}
                    data-testid={`upgrade-${p}`}
                  >
                    <Sparkles className="w-4 h-4 mr-1.5" /> Upgrade to {p}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-2xl p-6">
          <h3 className="font-display text-xl font-medium mb-1">Manage your plan</h3>
          <p className="text-sm text-stone-500 mb-4">You're on {sub.plan_label}. To change plans, switch to a different tier below.</p>
          <Button onClick={() => navigate(0)} variant="outline" className="rounded-full">Refresh</Button>
        </div>
      )}
    </div>
  );
}

function UsageBar({ feature, usage, limit }) {
  const unlimited = limit === null;
  const pct = unlimited ? 0 : Math.min(100, (usage / Math.max(1, limit)) * 100);
  const over = !unlimited && usage >= limit;
  return (
    <div className="border border-stone-200 rounded-xl p-3" data-testid={`usage-${feature}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-stone-700">{FEATURE_LABEL[feature]}</span>
        <span className={`tabular-nums ${over ? "text-red-700 font-semibold" : "text-stone-500"}`}>
          {usage}{unlimited ? "" : ` / ${limit}`}{!unlimited && FEATURE_SUFFIX[feature]}
        </span>
      </div>
      {!unlimited && (
        <div className="h-1.5 mt-2 bg-stone-100 rounded-full overflow-hidden">
          <div className={`h-full transition-all duration-500 ${over ? "bg-red-500" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
        </div>
      )}
      {unlimited && <div className="text-[10px] uppercase tracking-[0.14em] text-emerald-700 font-bold mt-1">Unlimited</div>}
    </div>
  );
}
